import React, { useState, useEffect } from 'react';
import { Modal, Form, Alert } from 'react-bootstrap';
import Button from 'components/base/Button';
import { getAvailableAssets, Asset } from 'api/assetsAPI';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import axiosClient from 'api/axiosClient';

interface AssignAssetModalProps {
  show: boolean;
  handleClose: () => void;
  requestId: number;
  categoryId: number;
  requestInfo?: {
    asset_name?: string;
    user_name?: string;
    category_name?: string;
    company_id?: number;
    company_name?: string;
  };
  onAssignSuccess: () => void;
}

const AssignAssetModal: React.FC<AssignAssetModalProps> = ({
  show,
  handleClose,
  requestId,
  categoryId,
  requestInfo,
  onAssignSuccess
}) => {
  const authUser = useSelector((state: RootState) => state.auth);
  const isSuperUser = authUser.role === 'super_user';

  // Use the company_id from the request if available
  const requestCompanyId = requestInfo?.company_id || 0;

  const [availableAssets, setAvailableAssets] = useState<Asset[]>([]);
  const [selectedAssetId, setSelectedAssetId] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAllCategories, setShowAllCategories] = useState(false);

  // Load available assets when modal opens
  useEffect(() => {
    if (show) {
      setSelectedAssetId(0);
      setError(null);

      if (requestCompanyId > 0) {
        // Load assets for the request's company
        loadAvailableAssets(requestCompanyId);
      } else if (!isSuperUser) {
        // For company admins without explicit company_id, backend auto-filters by their company
        loadAvailableAssets();
      } else {
        // No company context available
        setAvailableAssets([]);
        setError('Unable to determine company for this request.');
      }
    }
  }, [show, requestCompanyId, isSuperUser, showAllCategories]);

  // Helper function to check if category is "Others"
  const isOthersCategory = (categoryName?: string) => {
    if (!categoryName) return false;
    const normalized = categoryName.toLowerCase().trim();
    return ['others', 'other', 'misc', 'miscellaneous'].includes(normalized);
  };

  const loadAvailableAssets = async (companyId?: number) => {
    setLoadingAssets(true);
    setError(null);
    try {
      // Auto-enable showAllCategories for "Others" category or when explicitly toggled
      const shouldShowAll =
        isOthersCategory(requestInfo?.category_name) || showAllCategories;

      const assets = await getAvailableAssets(categoryId, {
        ...(companyId && { company_id: companyId }),
        ...(shouldShowAll && { include_all_categories: true })
      });

      setAvailableAssets(assets || []);
    } catch (err) {
      // Extract error message from backend
      const axiosError = err as {
        response?: {
          data?: { detail?: string | { message?: string; error?: string } };
        };
      };

      // Only show what the backend sends
      let errorMessage = 'Failed to load available assets.';

      if (axiosError.response?.data?.detail) {
        const detail = axiosError.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (detail.message) {
          errorMessage = detail.message;
        }
      }

      setError(errorMessage);
      setAvailableAssets([]);
    } finally {
      setLoadingAssets(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedAssetId || selectedAssetId === 0) {
      setError('Please select an asset');
      return;
    }

    setLoading(true);
    try {
      await axiosClient.put(
        `/requests/${requestId}/assign-asset`,
        {},
        {
          params: {
            asset_id: selectedAssetId
          }
        }
      );

      onAssignSuccess();
      handleClose();
    } catch (err: unknown) {
      console.error('Failed to assign asset:', err);
      const errorMessage =
        (err as { response?: { data?: { detail?: string; message?: string } } })
          ?.response?.data?.detail ||
        (err as { response?: { data?: { detail?: string; message?: string } } })
          ?.response?.data?.message ||
        'Failed to assign asset. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setSelectedAssetId(0);
    setAvailableAssets([]);
    setError(null);
    setShowAllCategories(false); // Reset the toggle when closing
    handleClose();
  };

  return (
    <Modal show={show} onHide={handleModalClose} centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fs-6">Assign Asset to Request</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          {/* Request Information */}
          <div className="mb-3 p-3 bg-body-highlight rounded">
            <h6 className="mb-2 fs-7">Request Information</h6>
            <div className="row">
              <div className="col-6">
                <small className="text-body-tertiary d-block">Request ID</small>
                <div className="fw-semibold fs-8">
                  REQ-{requestId.toString().padStart(5, '0')}
                </div>
              </div>
              {requestInfo?.company_name && (
                <div className="col-6">
                  <small className="text-body-tertiary d-block">Company</small>
                  <div className="fs-8">{requestInfo.company_name}</div>
                </div>
              )}
              {requestInfo?.user_name && (
                <div className="col-6 mt-2">
                  <small className="text-body-tertiary d-block">
                    Requested By
                  </small>
                  <div className="fs-8">{requestInfo.user_name}</div>
                </div>
              )}
              {requestInfo?.asset_name && (
                <div className="col-6 mt-2">
                  <small className="text-body-tertiary d-block">
                    Asset Type
                  </small>
                  <div className="fs-8">{requestInfo.asset_name}</div>
                </div>
              )}
              {requestInfo?.category_name && (
                <div className="col-6 mt-2">
                  <small className="text-body-tertiary d-block">Category</small>
                  <div className="fs-8">{requestInfo.category_name}</div>
                </div>
              )}
            </div>
          </div>

          {/* Category Filter Toggle */}
          {!isOthersCategory(requestInfo?.category_name) &&
            requestInfo?.category_name && (
              <div className="mb-3 p-3 bg-body-highlight rounded">
                <Form.Check
                  type="checkbox"
                  id="showAllCategories"
                  label={`Show all available assets (not just ${requestInfo.category_name})`}
                  checked={showAllCategories}
                  onChange={e => setShowAllCategories(e.target.checked)}
                  disabled={loadingAssets || loading}
                />
                <Form.Text className="text-muted d-block mt-1">
                  {showAllCategories
                    ? 'Showing all available assets from all categories'
                    : `Showing only ${requestInfo.category_name}`}
                </Form.Text>
              </div>
            )}

          {/* Asset Selection */}
          <Form.Group className="mb-3">
            <Form.Label>Select Available Asset *</Form.Label>
            <Form.Select
              value={selectedAssetId}
              onChange={e => setSelectedAssetId(Number(e.target.value))}
              required
              disabled={loadingAssets || loading}
            >
              <option value={0}>
                {loadingAssets ? 'Loading assets...' : 'Select an asset'}
              </option>
              {availableAssets.map(asset => (
                <option key={asset.id} value={asset.id}>
                  {asset.name} ({asset.category_name || 'N/A'}) -{' '}
                  {asset.serial_number} - {asset.brand} {asset.model}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Only available assets are shown in the list
            </Form.Text>
          </Form.Group>

          {/* Show message when no assets available (but no error) */}
          {availableAssets.length === 0 && !loadingAssets && !error && (
            <Alert variant="warning" className="mb-0">
              <i className="fas fa-exclamation-triangle me-2"></i>
              No available assets found in the company. All assets are currently
              assigned or unavailable.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top-0">
          <Button
            variant="secondary"
            onClick={handleModalClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={
              loading ||
              loadingAssets ||
              availableAssets.length === 0 ||
              selectedAssetId === 0
            }
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Assigning...
              </>
            ) : (
              'Assign Asset'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AssignAssetModal;
